import * as Yup from "yup";

import { Modal, OfficeButton } from "components";
import UpdateTargetBadgeForm, {
  UpdateTargetBadgeFormValues,
} from "components/forms/update-target-badge";

import { LocationGenerics } from "router/location";
import toast from "react-hot-toast";
import { updateTargetBadge } from "../../_graphql/visal/mutations/target-badge";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useSearch } from "react-location";
import { useTargetBadge } from "../../hooks/data/use-target-badge";
import { wrapClick } from "utils";

const giftSchema = Yup.object().shape({
  gift_name: Yup.string().required("Gift name is required"),
  gift_type: Yup.string().required("Gift type is required"),
  gift_image: Yup.string().url("Must be a valid URL"),
  gift_description: Yup.string().required("Gift description is required"),
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Badge name is required"),
  description: Yup.string(),
  min_points: Yup.number()
    .required("Minimum points required")
    .min(0, "Must be at least 0"),
  max_points: Yup.number()
    .required("Maximum points required")
    .min(0, "Must be at least 0"),
  cash_equivalent: Yup.number()
    .required("Cash equivalent required")
    .min(0, "Must be at least 0"),
  minimum_cash_equivalent: Yup.number()
    .required("Cash equivalent required")
    .min(0, "Must be at least 0"),
  badgeId: Yup.string().required(),
  gift_basket: Yup.array().of(giftSchema).max(5, "Maximum 5 gifts allowed"),
});

export default function UpdateTargetBadgeContainer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const { loading, badge, refetch } = useTargetBadge({
    badgeId: searchParams?.id || "",
  });

  const [execute, { loading: updating }] = useMutation(updateTargetBadge, {
    refetchQueries: ["fetchTargetBadge", "fetchTarget"],
  });

  const form = useFormik<UpdateTargetBadgeFormValues>({
    initialValues: {
      name: "",
      description: "",
      min_points: 0,
      max_points: 0,
      cash_equivalent: 0,
      minimum_cash_equivalent: 0,
      badgeId: searchParams?.id || "",
      gift_basket: [],
    },
    validationSchema,
    onSubmit: async (variables, helpers) => {
      const cleanGiftBasket = variables.gift_basket.map((gift) => {
        const { __typename, ...cleanGift } = gift as any;
        return cleanGift;
      });

      execute({
        variables: {
          targetBadgeId: variables.badgeId,
          badge: {
            name: badge?.name || "",
            description: badge?.description || "",
            min_points: badge?.min_points || 0,
            max_points: badge?.max_points || 0,
            cash_equivalent: variables.cash_equivalent,
            minimum_cash_equivalent: variables.minimum_cash_equivalent || 0,
            gift_basket: cleanGiftBasket,
          },
        },
      })
        .then(({ data }) => {
          if (data?.updateTargetBadge) {
            toast.success("Badge updated successfully");
            setOpen(false);
            helpers.resetForm();
            refetch();
          } else {
            toast.error("Failed to update Badge");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (badge) {
      const cleanGiftBasket = badge.gift_basket
        ? badge.gift_basket.map((gift) => {
            const { __typename, ...cleanGift } = gift as any;
            return cleanGift;
          })
        : [];

      form.setValues({
        name: badge.name || "",
        description: badge.description || "",
        min_points: badge.min_points || 0,
        max_points: badge.max_points || 0,
        cash_equivalent: badge.cash_equivalent || 0,
        minimum_cash_equivalent: badge.minimum_cash_equivalent || 0,
        badgeId: searchParams?.id || "",
        gift_basket: cleanGiftBasket,
      });
    }
  }, [badge]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      title="Update Badge"
      description="Update cash equivalent value (GHS ₵) and gift items for this badge"
      renderActions={() => (
        <OfficeButton
          type="button"
          size="sm"
          loading={updating}
          disabled={updating}
          onClick={wrapClick(form.handleSubmit)}
        >
          {updating ? "Please wait..." : "Update Badge"}
        </OfficeButton>
      )}
    >
      <UpdateTargetBadgeForm form={form} />
    </Modal>
  );
}
